import { Backdrop, Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import Header from '../../components/Header';
import { greenColor } from '../../config/colors';

import { toast, ToastContainer } from 'react-toastify';
import { useCookiesLogin } from '../../config/cookies';
import { tokens } from '../../theme';
import { useTranslation } from '../../hooks/useTranslation';
import ProposalCard from '../../components/ProposalCard';
import StatsCard from '../../components/StatsCard';
import { DollarSign, Plus, ShoppingCartIcon, UserRoundPlus, ZoomIn } from 'lucide-react';
import SimulationsChart from '../../components/SimulationsChart';
import ClientTable from '../../components/ClientTable';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import CompletarCadastroModal from '../onboarding/ModalCompleteRegister';
import ModalRequestAccessSuccess from '../onboarding/ModalRequestAccessSuccess';
import { useAccessStage } from '../../context/AccessStageContext';

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { cookies } = useCookiesLogin();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [clients, setClients] = useState(0);
  const [balance, setBalance] = useState(0);
  const [ordersLast30Days, setOrdersLast30Days] = useState(0);
  const [recentClients, setRecentClients] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [proposalStats, setProposalStats] = useState({
    approved: [],
    rejected: [],
    open: [],
    finished: [],
  });

  const toastShownRef = useRef(false);
  const approvalRate = proposals.length > 0 ? proposalStats.approved.length / proposals.length : 0;

  const { accessStage, setAccessStage } = useAccessStage();
  const [requestAccessOpen, setRequestAccessOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const isRestricted = accessStage !== 'APPROVED';

  const fetchDashboard = useCallback(async () => {
    try {
      setIsLoading(true);
      setHasError(false);

      const { data } = await api.get('/v1/dashboard');

      setClients(data.clients);
      setBalance(data.balance);
      setOrdersLast30Days(data.orders_last_30_days);
      setRecentClients(data.last_orders);

      if (data.balance <= 5 && !toastShownRef.current) {
        toast.info(t('messages.buyMoreTokens'), { theme: 'dark' });
        toastShownRef.current = true;
      }
    } catch (err) {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!cookies?.AUTH_TOKEN) return;
    if (isRestricted) {
      setIsLoading(false);
      return;
    }
    fetchDashboard();
  }, [cookies?.AUTH_TOKEN, isRestricted, fetchDashboard]);

  if (!cookies.AUTH_TOKEN) {
    return (
      <Backdrop open={true} style={{ zIndex: 9999 }}>
        <CircularProgress sx={{ color: greenColor }} />
      </Backdrop>
    );
  }

  if (isLoading) {
    return (
      <Backdrop open={true} style={{ zIndex: 9999 }}>
        <CircularProgress sx={{ color: greenColor }} />
      </Backdrop>
    );
  }

  if (hasError) {
    return (
      <Box m="20px">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={{ xs: '12px', md: 0 }}
          mb="20px"
          flexShrink={0}
        >
          <Header
            title={t('navigation.dashboard').toUpperCase()}
            subtitle={t('dashboard.welcomeMessage')}
          />

          <Link to="/app/redutoconsultas">
            <Button
              variant="contained"
              startIcon={<ShoppingCartIcon />}
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: colors.greenAccent[700],
                },
              }}
            >
              {t('dashboard.buyConsults')}
            </Button>
          </Link>
        </Box>
        <Typography color="error">{t('messages.errorLoading')}</Typography>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <ToastContainer />

      <CompletarCadastroModal
        open={requestAccessOpen}
        isRequestAccess
        onClose={() => setRequestAccessOpen(false)}
        onSuccess={() => {
          setRequestAccessOpen(false);
          setSuccessOpen(true);
          setAccessStage('PENDING');
        }}
      />
      <ModalRequestAccessSuccess open={successOpen} onClose={() => setSuccessOpen(false)} />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          mb: { xs: 4 },
        }}
      >
        <Header title={t('navigation.dashboard')} subtitle={t('dashboard.welcomeMessage')} />

        <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'nowrap' }}>
          {accessStage === 'NONE' && (
            <Button
              variant="outlined"
              onClick={() => setRequestAccessOpen(true)}
              sx={{
                borderColor: colors.greenAccent[600],
                color: colors.greenAccent[500],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: colors.greenAccent[700],
                  backgroundColor: 'transparent',
                },
              }}
            >
              {t('dashboard.requestFullAccess')}
            </Button>
          )}

          <Link to="/app/redutoconsultas">
            <Button
              variant="contained"
              startIcon={<ShoppingCartIcon />}
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: colors.greenAccent[700],
                },
              }}
            >
              {t('dashboard.buyConsults')}
            </Button>
          </Link>
        </Box>
      </Box>

      {!isRestricted && (
        <Box
          sx={{
            mb: '20px',
            display: 'grid',
            gap: '20px',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
          }}
        >
          <ProposalCard
            title={t('dashboard.proposals')}
            value={proposals.length}
            subtitle={t('dashboard.approved')}
            progressValue={approvalRate}
            progressLabel={`${(approvalRate * 100).toFixed(0)}% Aprovadas`}
          />

          <StatsCard
            title={t('dashboard.clients')}
            value={clients}
            subtitle={
              <>
                <Plus size={16} color={colors.greenAccent[500]} />
                {t('dashboard.newRegister')}
              </>
            }
            variant="inline"
            icon={<UserRoundPlus />}
          />

          <StatsCard
            title={t('dashboard.completedConsults')}
            value={ordersLast30Days}
            subtitle="Em 30 dias"
            icon={<ZoomIn />}
          />

          <StatsCard
            title={t('dashboard.remainingConsults')}
            value={balance}
            subtitle={t('dashboard.consults')}
            icon={<DollarSign />}
          />
        </Box>
      )}

      {!isRestricted && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: '1fr',
              lg: '60% 1fr',
            },
            gap: '20px',
            mb: '20px',
          }}
        >
          <SimulationsChart proposalStats={proposalStats} />
          <ClientTable clients={recentClients} />
        </Box>
      )}

      {isRestricted && (
        <Box
          sx={{
            mt: 4,
            mx: 'auto',
            maxWidth: 480,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {accessStage === 'PENDING' && (
            <>
              <Typography variant="h6" fontWeight={700} color="text.primary">
                {t('dashboard.pendingTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.pendingDescription')}
              </Typography>
            </>
          )}

          {accessStage === 'REJECTED' && (
            <>
              <Typography variant="h6" fontWeight={700} color="error">
                {t('dashboard.rejectedTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.rejectedDescription')}
              </Typography>
            </>
          )}

          {accessStage === 'NONE' && (
            <>
              <Typography variant="h6" fontWeight={700} color="text.primary">
                {t('dashboard.noneTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.noneDescription')}
              </Typography>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
